// Phase 10 · Assessment Scenario Bank — Admin tab.
// Lists variants per (concept, env), opens the Add Variant modal, and
// surfaces approve / retire / delete. Candidate flow is unaffected.
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, ChevronRight, Shield, Trash2, Check, Archive } from "lucide-react";
import VariantForm from "./VariantForm";

const STATUS_PILLS = {
  active:   { label: "ACTIVE",   cls: "bg-emerald-100 text-emerald-800" },
  default:  { label: "DEFAULT",  cls: "bg-[var(--iddl-surface)] text-[var(--iddl-ink)] border border-[var(--iddl-ink)]" },
  approved: { label: "APPROVED", cls: "bg-blue-100 text-blue-800" },
  draft:    { label: "DRAFT",    cls: "bg-amber-100 text-amber-800" },
  retired:  { label: "RETIRED",  cls: "bg-zinc-200 text-zinc-600" },
};

function StatusPill({ v }) {
  // Phase 11.2 · the authoring lifecycle pill AND the deployment pill
  // are rendered side-by-side so admins can tell at a glance which
  // variant is live for this (concept, environment).
  const authoringLabel =
    v.is_default ? "DEFAULT"
    : (STATUS_PILLS[v.status] || STATUS_PILLS.draft).label;
  const authoringCls =
    v.is_default ? STATUS_PILLS.default.cls
    : (STATUS_PILLS[v.status] || STATUS_PILLS.draft).cls;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span
        className={`label-mono text-[10px] px-2 py-0.5 ${authoringCls}`}
        data-testid={`variant-status-${v.variant_id}`}>
        {authoringLabel}
      </span>
      {v.is_active ? (
        <span
          className="label-mono text-[10px] px-2 py-0.5 border border-emerald-400 text-emerald-800 bg-emerald-50"
          data-testid={`variant-active-${v.variant_id}`}>
          ACTIVE
        </span>
      ) : (
        <span
          className="label-mono text-[10px] px-2 py-0.5 border border-zinc-300 text-zinc-500 bg-white"
          data-testid={`variant-inactive-${v.variant_id}`}>
          NOT ACTIVE
        </span>
      )}
    </span>
  );
}

export default function ScenarioBank() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addCtx, setAddCtx] = useState(null); // {concept_id, environment, concept_name, primitive}

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/variants?include_retired=true");
      setRows(r.data.variants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load variants");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  // Group by concept_id → environment → [variants]
  const grouped = useMemo(() => {
    const g = {};
    for (const v of rows) {
      g[v.concept_id] = g[v.concept_id] || { name: v.concept_name, domain: v.domain, subdomain: v.subdomain, envs: {} };
      const envKey = v.environment ?? "null";
      g[v.concept_id].envs[envKey] = g[v.concept_id].envs[envKey] || { env: v.environment, primitive: v.primitive, variants: [] };
      g[v.concept_id].envs[envKey].variants.push(v);
    }
    return g;
  }, [rows]);

  const conceptIds = useMemo(() => Object.keys(grouped).sort(), [grouped]);
  const totals = useMemo(() => {
    const defs = rows.filter(r => r.is_default).length;
    const nonDef = rows.length - defs;
    const drafts = rows.filter(r => !r.is_default && r.status === "draft").length;
    return { total: rows.length, defs, nonDef, drafts };
  }, [rows]);

  const doApprove = async (vid) => { try { await api.post(`/admin/variants/${vid}/approve`); toast.success("Approved"); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); } };
  // Phase 11.2 · deployment-level activation. Requires explicit admin
  // confirmation and will demote the currently-Active variant for the
  // same (concept_id, environment) to Not Active.
  const doActivate = async (v) => {
    if (!confirm(
      `Activate variant ${v.variant_id}\n\n(${v.variant_name})\n\n` +
      `This will become the Active variant for ${v.concept_id} · env=${v.environment || "null"}.\n` +
      `Any currently-Active variant for the same concept/environment will become Not Active.\n\n` +
      `Candidate attempts that are already in progress will remain bound to whatever variant they started with. ` +
      `Only NEW candidate attempts will pick this variant up.`,
    )) return;
    try {
      await api.post(`/admin/variants/${v.variant_id}/activate`);
      toast.success("Variant activated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activate failed");
    }
  };
  const doRevert  = async (vid) => { try { await api.post(`/admin/variants/${vid}/revert_to_draft`); toast.success("Reverted to Draft"); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Revert failed"); } };
  const doRetire  = async (vid) => { if (!confirm("Retire this variant? It will no longer be usable for new preview runs.")) return; try { await api.post(`/admin/variants/${vid}/retire`); toast.success("Retired"); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Retire failed"); } };
  const doDelete  = async (vid) => { if (!confirm("Delete this draft variant? This cannot be undone.")) return; try { await api.delete(`/admin/variants/${vid}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } };
  // Phase 11.1 · retired-variant deletion. Server-side rule enforces
  // non-default + usage_count==0 + no real candidate-attempt reference.
  const doDeleteRetired = async (v) => {
    if (!confirm(
      `Delete retired variant ${v.variant_id}\n\n` +
      `(${v.variant_name})\n\n` +
      `This permanently removes the variant from the Scenario Bank. ` +
      `Only allowed when the variant has never been used by a candidate ` +
      `(usage_count = 0) and is not referenced by any attempt. ` +
      `This cannot be undone.`,
    )) return;
    try {
      await api.delete(`/admin/variants/${v.variant_id}`);
      toast.success("Retired variant deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="paper p-6 flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading variants…</div>;

  return (
    <div className="space-y-4" data-testid="scenario-bank">
      <div className="flex items-center flex-wrap gap-3">
        <div className="text-sm text-[var(--iddl-muted)]">
          <strong>{totals.defs}</strong> Default variants · <strong>{totals.nonDef}</strong> additional · <strong>{totals.drafts}</strong> drafts
        </div>
        <button
          type="button"
          onClick={() => { setAddCtx(null); setShowAdd(true); }}
          data-testid="btn-add-variant-global"
          className="btn-ink ms-auto px-3 py-1.5 text-sm inline-flex items-center gap-2"
        ><Plus size={14}/> Add Variant</button>
      </div>

      <div className="border border-[var(--iddl-rule)] divide-y divide-[var(--iddl-rule)]" data-testid="scenario-bank-list">
        {conceptIds.map(cid => {
          const g = grouped[cid];
          const isOpen = !!open[cid];
          const envKeys = Object.keys(g.envs).sort();
          return (
            <div key={cid}>
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--iddl-paper)]"
                data-testid={`sb-row-${cid}`}
                onClick={() => setOpen({...open, [cid]: !isOpen})}
              >
                <ChevronRight size={14} className={`transition-transform ${isOpen ? "rotate-90" : ""}`}/>
                <code className="label-mono">{cid}</code>
                <span className="font-display font-semibold">{g.name}</span>
                <span className="label-mono text-[var(--iddl-muted)] hidden md:inline">{g.subdomain}</span>
                <span className="ms-auto label-mono text-xs text-[var(--iddl-muted)]">
                  {Object.values(g.envs).reduce((s, e) => s + e.variants.length, 0)} variant(s)
                </span>
              </button>
              {isOpen && (
                <div className="bg-[var(--iddl-paper)] px-4 py-3 space-y-3">
                  {envKeys.map(ek => {
                    const envRow = g.envs[ek];
                    return (
                      <div key={ek} className="border border-[var(--iddl-rule)] bg-white">
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--iddl-rule)]">
                          <span className="label-mono text-xs">Env: {envRow.env === "agentic_ai" ? "Agentic AI" : envRow.env === "ai" ? "AI" : "—"}</span>
                          <code className="label-mono text-xs text-[var(--iddl-muted)]">{envRow.primitive}</code>
                          <button type="button"
                            className="ms-auto text-xs px-2 py-1 border border-[var(--iddl-rule)] inline-flex items-center gap-1 hover:bg-[var(--iddl-surface)]"
                            data-testid={`btn-add-variant-${cid}-${ek}`}
                            onClick={() => { setAddCtx({ concept_id: cid, environment: envRow.env, concept_name: g.name, primitive: envRow.primitive }); setShowAdd(true); }}
                          ><Plus size={12}/> Add Variant for this env</button>
                        </div>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-[var(--iddl-muted)] bg-[var(--iddl-surface)]">
                              <th className="px-3 py-1.5">Variant</th>
                              <th className="px-3 py-1.5">Country</th>
                              <th className="px-3 py-1.5">Status</th>
                              <th className="px-3 py-1.5">Updated</th>
                              <th className="px-3 py-1.5 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {envRow.variants.map(v => (
                              <tr key={v.variant_id} className="border-t border-[var(--iddl-rule)]" data-testid={`variant-row-${v.variant_id}`}>
                                <td className="px-3 py-1.5">
                                  <div className="font-medium">{v.variant_name}</div>
                                  <code className="label-mono text-xs text-[var(--iddl-muted)]">{v.variant_id}</code>
                                </td>
                                <td className="px-3 py-1.5">{v.country || "—"}</td>
                                <td className="px-3 py-1.5"><StatusPill v={v}/></td>
                                <td className="px-3 py-1.5 text-xs text-[var(--iddl-muted)]">{(v.approved_at || v.created_at || "").slice(0,10)}</td>
                                <td className="px-3 py-1.5 text-right">
                                  {/* Phase 11.2 · Activate action is
                                      allowed on any non-Active Default
                                      or Approved variant. Retired and
                                      Draft rows are not activatable. */}
                                  {!v.is_active && (v.is_default || v.status === "approved") && (
                                    <button
                                      className="text-xs px-2 py-1 border border-emerald-500 text-emerald-800 inline-flex items-center gap-1 hover:bg-emerald-50"
                                      onClick={() => doActivate(v)}
                                      data-testid={`btn-activate-${v.variant_id}`}
                                      title="Activate this variant for its (concept_id, environment). The previously-Active variant becomes Not Active.">
                                      <Check size={12}/> Activate
                                    </button>
                                  )}
                                  {!v.is_default && v.status === "draft" && (
                                    <>
                                      <button className="ms-2 text-xs px-2 py-1 border border-[var(--iddl-rule)] inline-flex items-center gap-1 hover:bg-emerald-50"
                                        onClick={() => doApprove(v.variant_id)} data-testid={`btn-approve-${v.variant_id}`}>
                                        <Check size={12}/> Approve
                                      </button>
                                      <button className="ms-2 text-xs px-2 py-1 border border-[var(--iddl-rule)] inline-flex items-center gap-1 hover:bg-red-50"
                                        onClick={() => doDelete(v.variant_id)} data-testid={`btn-delete-${v.variant_id}`}>
                                        <Trash2 size={12}/> Delete
                                      </button>
                                    </>
                                  )}
                                  {!v.is_default && v.status === "approved" && (
                                    <>
                                      <button className="text-xs px-2 py-1 border border-[var(--iddl-rule)] inline-flex items-center gap-1 hover:bg-amber-50"
                                        onClick={() => doRevert(v.variant_id)} data-testid={`btn-revert-${v.variant_id}`}
                                        title="Return this variant to Draft so it can be edited again. Re-approving locks it.">
                                        Revert to Draft
                                      </button>
                                      <button className="ms-2 text-xs px-2 py-1 border border-[var(--iddl-rule)] inline-flex items-center gap-1 hover:bg-zinc-100"
                                        onClick={() => doRetire(v.variant_id)} data-testid={`btn-retire-${v.variant_id}`}>
                                        <Archive size={12}/> Retire
                                      </button>
                                    </>
                                  )}
                                  {!v.is_default && v.status === "retired" && (
                                    (Number(v.usage_count || 0) === 0) ? (
                                      <button
                                        className="text-xs px-2 py-1 border border-rose-600 text-rose-700 inline-flex items-center gap-1 hover:bg-rose-50"
                                        onClick={() => doDeleteRetired(v)}
                                        data-testid={`btn-delete-retired-${v.variant_id}`}
                                        title="Delete this retired variant permanently. Only enabled when usage_count = 0 and no candidate attempt references it.">
                                        <Trash2 size={12}/> Delete
                                      </button>
                                    ) : (
                                      <span
                                        className="text-xs text-[var(--iddl-muted)] italic"
                                        data-testid={`del-retired-blocked-${v.variant_id}`}
                                        title={`Cannot delete: usage_count=${v.usage_count || 0}. Retired variants that were used by candidates are kept for audit.`}>
                                        kept for audit
                                      </span>
                                    )
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {showAdd && (
        <VariantForm
          preset={addCtx}
          onCancel={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </div>
  );
}
